import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

type AccessLevel = 'none' | 'read_only' | 'read_write' | 'own_client_only';
type PermissionsMap = Record<string, AccessLevel>;

interface PermissionsContextValue {
  role: string | null;
  isSuperAdmin: boolean;
  permissions: PermissionsMap;
  isLoading: boolean;
  can: (feature: string, needed?: 'read' | 'write') => boolean;
  refresh: () => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextValue | null>(null);

const LEVEL_RANK: Record<AccessLevel, number> = {
  none: 0,
  read_only: 1,
  own_client_only: 2,
  read_write: 2,
};

export const PermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [role, setRole] = useState<string | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [permissions, setPermissions] = useState<PermissionsMap>({});
  const [isLoading, setIsLoading] = useState(true);

  const fetchPermissions = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setPermissions({});
        setIsLoading(false);
        return;
      }
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const res = await axios.get(`${apiUrl}/api/permissions/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.success) {
        setRole(res.data.role || null);
        setIsSuperAdmin(!!res.data.isSuperAdmin);
        setPermissions(res.data.permissions || {});
      }
    } catch (err) {
      console.warn('Failed to load permissions:', err);
      setPermissions({});
    } finally {
      setIsLoading(false);
    }
  }, []);

    useEffect(() => {
    fetchPermissions();

    const handleAuthChange = () => {
      console.log('🔄 Auth changed, refetching permissions...');
      fetchPermissions();
    };

    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'token') {
        console.log('🔄 Token changed in storage, refetching permissions...');
        fetchPermissions();
      }
    };

    window.addEventListener('auth-changed', handleAuthChange);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('auth-changed', handleAuthChange);
      window.removeEventListener('storage', handleStorage);
    };
  }, [fetchPermissions]);

  const can = useCallback(
    (feature: string, needed: 'read' | 'write' = 'read') => {
      if (isSuperAdmin) return true;
      const level: AccessLevel = permissions[feature] ?? 'none';
      const requiredRank = needed === 'write' ? 2 : 1;
      return LEVEL_RANK[level] >= requiredRank;
    },
    [permissions, isSuperAdmin]
  );

  return (
    <PermissionsContext.Provider value={{ role, isSuperAdmin, permissions, isLoading, can, refresh: fetchPermissions }}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = (): PermissionsContextValue => {
  const ctx = useContext(PermissionsContext);
  if (!ctx) throw new Error('usePermissions must be used inside PermissionsProvider');
  return ctx;
};